import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { JULY_FOURTH_COLORS } from '../../utils/seasonalCelebrationLogic';
import AmericanFlag from './AmericanFlag';
import './JulyFourthCelebration.css';

const DURATION_MS = 11_500;
const REDUCED_MOTION_MS = 8_000;
const FADE_MS = 1_400;

type ConfettiPiece = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  rot: number;
  rotV: number;
  color: string;
  opacity: number;
  shape: 'rect' | 'strip';
};

type Spark = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
};

type Rocket = {
  x: number;
  y: number;
  vy: number;
  targetY: number;
  color: string;
  trail: Array<{ x: number; y: number; alpha: number }>;
};

function pickColor(): string {
  return JULY_FOURTH_COLORS[Math.floor(Math.random() * JULY_FOURTH_COLORS.length)]!;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function spawnConfetti(width: number, count: number): ConfettiPiece[] {
  return Array.from({ length: count }, () => ({
    x: Math.random() * width,
    y: -12 - Math.random() * 80,
    vx: (Math.random() - 0.5) * 2.4,
    vy: 2.2 + Math.random() * 3.6,
    w: 4 + Math.random() * 8,
    h: 6 + Math.random() * 14,
    rot: Math.random() * Math.PI * 2,
    rotV: (Math.random() - 0.5) * 0.22,
    color: pickColor(),
    opacity: 0.85 + Math.random() * 0.15,
    shape: Math.random() > 0.35 ? 'strip' : 'rect',
  }));
}

function explodeFirework(x: number, y: number, color: string): Spark[] {
  const count = 48 + Math.floor(Math.random() * 36);
  return Array.from({ length: count }, () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = 1.8 + Math.random() * 5.2;
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 0,
      maxLife: 38 + Math.random() * 42,
      color: Math.random() > 0.25 ? color : pickColor(),
      size: 1.4 + Math.random() * 2.4,
    };
  });
}

function launchRocket(width: number, height: number): Rocket {
  return {
    x: width * (0.12 + Math.random() * 0.76),
    y: height + 4,
    vy: -(7.5 + Math.random() * 4.5),
    targetY: height * (0.18 + Math.random() * 0.42),
    color: pickColor(),
    trail: [],
  };
}

interface JulyFourthCelebrationProps {
  onComplete: () => void;
}

const JulyFourthCelebration: React.FC<JulyFourthCelebrationProps> = ({ onComplete }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const onCompleteRef = useRef(onComplete);
  const reducedMotion = prefersReducedMotion();
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (reducedMotion) {
      const fadeTimer = window.setTimeout(() => {
        rootRef.current?.classList.add('july-fourth-celebration--fade-out');
      }, REDUCED_MOTION_MS - FADE_MS);
      const doneTimer = window.setTimeout(() => {
        onCompleteRef.current();
      }, REDUCED_MOTION_MS);
      return () => {
        window.clearTimeout(fadeTimer);
        window.clearTimeout(doneTimer);
      };
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const confetti: ConfettiPiece[] = spawnConfetti(width, 140);
    const sparks: Spark[] = [];
    const rockets: Rocket[] = [];
    let lastRocketMs = 0;
    let lastConfettiMs = 0;
    const startMs = performance.now();
    let raf = 0;
    let fading = false;

    const tick = (now: number) => {
      const elapsed = now - startMs;
      if (elapsed >= DURATION_MS - FADE_MS && !fading) {
        fading = true;
        rootRef.current?.classList.add('july-fourth-celebration--fade-out');
      }
      if (elapsed >= DURATION_MS) {
        window.removeEventListener('resize', resize);
        onCompleteRef.current();
        return;
      }

      if (now - lastRocketMs > 520 + Math.random() * 680) {
        rockets.push(launchRocket(width, height));
        lastRocketMs = now;
      }
      if (now - lastConfettiMs > 180) {
        confetti.push(...spawnConfetti(width, 6));
        lastConfettiMs = now;
      }

      ctx.clearRect(0, 0, width, height);

      // Rockets
      for (let i = rockets.length - 1; i >= 0; i -= 1) {
        const r = rockets[i]!;
        r.y += r.vy;
        r.trail.push({ x: r.x, y: r.y, alpha: 1 });
        if (r.trail.length > 14) r.trail.shift();
        for (let t = 0; t < r.trail.length; t += 1) {
          const pt = r.trail[t]!;
          const alpha = (t / r.trail.length) * 0.75;
          ctx.beginPath();
          ctx.fillStyle = `rgba(255, 220, 120, ${alpha})`;
          ctx.arc(pt.x, pt.y, 2.2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.beginPath();
        ctx.fillStyle = r.color;
        ctx.arc(r.x, r.y, 3, 0, Math.PI * 2);
        ctx.fill();

        if (r.y <= r.targetY) {
          sparks.push(...explodeFirework(r.x, r.y, r.color));
          rockets.splice(i, 1);
        }
      }

      // Firework sparks
      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const s = sparks[i]!;
        s.life += 1;
        s.x += s.vx;
        s.y += s.vy;
        s.vy += 0.07;
        s.vx *= 0.985;
        const t = s.life / s.maxLife;
        const alpha = Math.max(0, 1 - t * t);
        if (alpha <= 0.02) {
          sparks.splice(i, 1);
          continue;
        }
        ctx.beginPath();
        ctx.fillStyle = s.color;
        ctx.globalAlpha = alpha;
        ctx.arc(s.x, s.y, s.size * (1 - t * 0.35), 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      // Confetti
      for (let i = confetti.length - 1; i >= 0; i -= 1) {
        const c = confetti[i]!;
        c.x += c.vx + Math.sin(c.rot * 2) * 0.35;
        c.y += c.vy;
        c.vx *= 0.998;
        c.rot += c.rotV;
        if (c.y > height + 40) {
          confetti.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(c.rot);
        ctx.globalAlpha = c.opacity;
        ctx.fillStyle = c.color;
        if (c.shape === 'strip') {
          ctx.fillRect(-c.w / 2, -c.h / 2, c.w * 0.55, c.h);
        } else {
          ctx.fillRect(-c.w / 2, -c.h / 2, c.w, c.h * 0.65);
        }
        ctx.restore();
        ctx.globalAlpha = 1;
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [reducedMotion]);

  return createPortal(
    <div
      ref={rootRef}
      className={`july-fourth-celebration${reducedMotion ? ' july-fourth-celebration--reduced-motion' : ''}`}
      aria-hidden
    >
      {!reducedMotion && <canvas ref={canvasRef} />}
      <div className="july-fourth-celebration-heading">
        <div className="july-fourth-celebration-heading-inner">
          <div className="july-fourth-celebration-flag-wrap">
            <AmericanFlag className="july-fourth-celebration-flag" />
          </div>
          <p className="july-fourth-celebration-title">
            <span className="july-fourth-celebration-title-line">Happy Birthday</span>
            <span className="july-fourth-celebration-title-line july-fourth-celebration-title-line--america">
              America
            </span>
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default JulyFourthCelebration;
